import React, { useState, useEffect, useContext, useMemo } from 'react';

import Axios from 'axios';
import { Formik, Field } from 'formik';
import * as Yup from 'yup';

import styled from 'styled-components';

import { ListContext } from '@contexts/ListContext';

import { getContactName } from '@hooks/API/Contact/UseContactsList';

import { NwCancelButton, NwSaveButton, NwLinkButton } from '@components/Gui/NwButton';
import { NwCard } from '@components/Gui/NwCard/NwCard';
import { NWInput, NWTextAreaCharCounter, NWSwitch, NWSelect } from '@components/Gui/NwForm/NwFormItems';
import { NwForm, NwFormButtonsBar } from '@components/Gui/NwForm/NwFormWrapper';
import NwIcon from '@components/Gui/NwIcon';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const FormViewWrapper = styled.div`
    padding: 2rem 0;

    .connection-details-container {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin: 1rem 0;
        .connection-details {
            display: flex;
            align-items: center;
            line-height: 1.2;
            .connection-icon {
                margin-top: 4px;
                margin-left: 8px;
                margin-right: 16px;
                width: 32px;
                display: flex;
                justify-content: center;
            }
            svg {
                margin-right: 4px;
                margin-bottom: 2px;
                font-size: 0.8rem;
            }
            .name {
                font-weight: bold;
                font-size: 1.2rem;
                color: ${(props) => props.invisible && props.theme.DangerColor};
            }
            .description {
                text-transform: uppercase;
                font-size: 0.8rem;
                margin-top: 4px;
            }
        }
    }
`;

const AlternativeOptionsContainer = styled.div`
    margin-top: 0.5rem;
    margin-bottom: 1rem;

    .alternative-label {
        font-size: 0.8rem;
        color: #666;
        margin-bottom: 0.25rem;
    }

    .alternative-buttons {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
    }
`;

const PhoneButtonWithIcon = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;

    .phone-icon {
        font-size: 0.9rem;
        color: #666;
    }
`;

const emptyValues = {
    CallSheetTypeID: '',
    Name: '',
    Phone: '',
    Email: '',
    Note: '',
    Internal: false,
};

const PhoneTypeIcon = (type) => {
    if (!type) return byPrefixAndName.fasl['mobile'];
    switch (type.toLowerCase()) {
        case 'mobile':
            return byPrefixAndName.fasl['mobile'];
        case 'land':
            return byPrefixAndName.fasl['phone-office'];
        case 'fax':
            return byPrefixAndName.fasl['fax'];
        case 'other':
            return byPrefixAndName.fasl['phone'];
        default:
            return byPrefixAndName.fasl['mobile'];
    }
};

const AddCallSheetFormView = ({
    selectedConnection,
    onClose,
    onTouchForm,
    onUpdateCallSheet,
    callSheetType,
    projectID,
    callsheet,
    onChangeView,
}) => {
    const [callsheetConnection, setCallsheetConnection] = useState();
    const [initialValues, setInitialValues] = useState({ ...emptyValues, CallSheetTypeID: callSheetType });

    const { cachedList } = useContext(ListContext);
    const { callsheetTypes } = cachedList;

    const loadConnection = async (ID) => {
        try {
            const connection = await Axios.get(`/connections/${ID}`);
            setCallsheetConnection(connection);
        } catch (error) {
            console.error('Get Simple Connections error => ', error);
        }
    };

    const saveCallSheet = async (values, setSubmitting) => {
        if (callsheet) {
            const data = {
                ID: callsheet.ID,
                ConnectionID: callsheetConnection ? callsheetConnection.ID : null,
                ContactID: callsheetConnection ? callsheetConnection.ContactID : null,
                ...values,
            };
            try {
                await Axios.put('/callsheets', data);
                setSubmitting(false);
                if (onUpdateCallSheet) {
                    onUpdateCallSheet();
                }
                if (onClose) {
                    onClose();
                }
            } catch (error) {
                console.error('Update Callsheet Error => ', error);
                setSubmitting(false);
            }
        } else {
            const callSheetData = callsheetConnection
                ? {
                      ProjectID: projectID,
                      ConnectionID: callsheetConnection.ID,
                      ContactID: callsheetConnection.ContactID,
                      ...values,
                  }
                : {
                      ProjectID: projectID,
                      ...values,
                  };
            setSubmitting(true);
            try {
                await Axios.post('/callsheets', callSheetData);
                if (onUpdateCallSheet) {
                    onUpdateCallSheet();
                }
                if (onClose) {
                    onClose();
                }
            } catch (error) {
                console.error('Add Connection Error => ', error);
            } finally {
                setSubmitting(false);
            }
        }
    };

    const getFullName = (conn) => {
        if (!conn) return '';

        let fn = conn.Name;
        if (conn.Surname) {
            if (fn.length) {
                fn += ' ' + conn.Surname;
            }
        }
        return fn;
    };

    const unlinkContact = () => {
        setCallsheetConnection(null);
        //setInitialValues(emptyValues);
    };

    const linkContact = () => {
        onChangeView('search');
    };

    useEffect(() => {
        //UPDATE A CALLSHEET
        if (callsheet && !selectedConnection) {
            const newInitialValues = {
                CallSheetTypeID: callsheet.CallSheetType.ID,
                Internal: callsheet.Internal ? true : false,
                Name: callsheet.Name ? callsheet.Name : '',
                Phone: callsheet.Phone ? callsheet.Phone : '',
                Email: callsheet.Email ? callsheet.Email : '',
                Note: callsheet.Note ? callsheet.Note : '',
            };
            setInitialValues(newInitialValues);
            if (callsheet.Connection) {
                try {
                    loadConnection(callsheet.Connection.ID);
                } catch (error) {
                    console.error('Get Callsheet Connection error => ', error);
                }
            }
        }
    }, [callsheet]);

    useEffect(() => {
        //ADDING A NEW CALLSHEET WITH A SELECTED CONNECTION OR UPDATE A CALLSHEET WITH A SELECTED CONNECTION
        if (selectedConnection) {
            setCallsheetConnection(selectedConnection);
            const { Phones, Emails } = selectedConnection;
            const newInitialValues = {
                CallSheetTypeID: callsheet ? callsheet.CallSheetType.ID : callSheetType,
                Name: getFullName(selectedConnection),
                Phone: Phones && Phones.length ? Phones[0].Number : '',
                Email: Emails && Emails.length ? Emails[0].Address : '',
                Note: callsheet ? callsheet.Note : '',
                Internal: callsheet ? callsheet.Internal : false,
            };
            setInitialValues(newInitialValues);
        }
    }, [selectedConnection]);

    const callsheetConnectionFullName = useMemo(() => {
        if (!callsheetConnection) return '';
        let fn = callsheetConnection.Name;
        if (callsheetConnection.Surname) {
            if (fn.length) {
                fn += ' ' + callsheetConnection.Surname;
            }
        }
        return fn;
    }, [callsheetConnection]);

    const callsheetContactName = useMemo(() => {
        if (!callsheetConnection) return '';
        return getContactName(callsheetConnection.ContactID);
    }, [callsheetConnection]);

    return (
        <FormViewWrapper>
            <NwCard>
                {callsheetConnection ? (
                    <div className="connection-details-container">
                        <div className="connection-details">
                            {callsheetConnection.TypeName === 'People' ? (
                                <div>
                                    <div className="name">{callsheetConnectionFullName}</div>
                                    <div className="description">
                                        {callsheetConnection.Role && `${callsheetConnection.Role} @ `}
                                        {getContactName(callsheetConnection.ContactID)}
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <div className="name">{callsheetContactName}</div>
                                    {callsheetConnectionFullName !== callsheetContactName && (
                                        <div className="description">{callsheetConnectionFullName}</div>
                                    )}
                                </div>
                            )}
                        </div>
                        {/* <NwButton
                            icon={byPrefixAndName.fasl['link-slash']}
                            ghost
                            label="unlink contact"
                            onClick={unlinkContact}
                            primary
                        /> */}
                    </div>
                ) : (
                    <div className="connection-details-container">
                        <div className="connection-details">&nbsp;</div>
                        {/* <NwButton
                            icon={byPrefixAndName.fasl['link']}
                            ghost
                            label="link contact"
                            onClick={linkContact}
                            primary
                        /> */}
                    </div>
                )}

                <Formik
                    initialValues={initialValues}
                    enableReinitialize={true}
                    onSubmit={(values, { setSubmitting }) => {
                        setSubmitting(true);
                        saveCallSheet(values, setSubmitting);
                    }}
                    validationSchema={Yup.object().shape({
                        Name: Yup.string().required('Name is required'),
                    })}
                >
                    {({ isSubmitting, values, handleSubmit, setFieldValue }) => (
                        <NwForm values={values} onTouchForm={onTouchForm} onFinish={handleSubmit} layout="vertical">
                            {/* {callsheet && ( */}
                            <div>
                                <Field
                                    component={NWSelect}
                                    label="Callsheet Type"
                                    name="CallSheetTypeID"
                                    placeholder="Select a callsheet type"
                                    value={values.CallSheetTypeID}
                                    getPopupContainer={(triggerNode) => triggerNode.parentNode}
                                    options={callsheetTypes.items.map((callsheet) => {
                                        return { key: callsheet.ID, label: callsheet.Name, value: callsheet.ID };
                                    })}
                                />
                            </div>
                            {/* )} */}
                            <div>
                                <Field
                                    component={NWInput}
                                    label="Name"
                                    name="Name"
                                    maxLength={100}
                                    type="text"
                                    value={values.Name}
                                />
                            </div>
                            <div>
                                <Field
                                    component={NWInput}
                                    label="Phone"
                                    name="Phone"
                                    maxLength={50}
                                    type="text"
                                    value={values.Phone}
                                />
                                {callsheetConnection?.Phones?.length > 1 && (
                                    <AlternativeOptionsContainer>
                                        <div className="alternative-label">Alternative phone numbers:</div>
                                        <div className="alternative-buttons">
                                            {callsheetConnection.Phones.filter(
                                                (phone) => phone.Number !== values.Phone
                                            ).map((phone, index) => (
                                                <PhoneButtonWithIcon key={`phone-${index}`}>
                                                    <NwIcon icon={PhoneTypeIcon(phone.Type)} className="phone-icon" />
                                                    <NwLinkButton
                                                        label={phone.Number}
                                                        onClick={() => setFieldValue('Phone', phone.Number)}
                                                    />
                                                </PhoneButtonWithIcon>
                                            ))}
                                        </div>
                                    </AlternativeOptionsContainer>
                                )}
                            </div>
                            <div>
                                <Field
                                    component={NWInput}
                                    label="E-Mail"
                                    name="Email"
                                    maxLength={100}
                                    type="text"
                                    value={values.Email}
                                />
                                {callsheetConnection?.Emails?.length > 1 && (
                                    <AlternativeOptionsContainer>
                                        <div className="alternative-label">Alternative email addresses:</div>
                                        <div className="alternative-buttons">
                                            {callsheetConnection.Emails.filter(
                                                (email) => email.Address !== values.Email
                                            ).map((email, index) => (
                                                <NwLinkButton
                                                    key={`email-${index}`}
                                                    label={email.Address}
                                                    onClick={() => setFieldValue('Email', email.Address)}
                                                />
                                            ))}
                                        </div>
                                    </AlternativeOptionsContainer>
                                )}
                            </div>
                            <div>
                                <Field
                                    component={NWTextAreaCharCounter}
                                    label="Note"
                                    name="Note"
                                    type="text"
                                    value={values.Note}
                                    maxLength={300}
                                    autoSize={{ minRows: 5, maxRows: 5 }}
                                />
                            </div>
                            <div>
                                <Field
                                    component={NWSwitch}
                                    label="Hidden to models"
                                    name="Internal"
                                    defaultChecked={false}
                                    checked={values.Internal}
                                />
                            </div>
                            <br />
                            <NwFormButtonsBar
                                left={<NwCancelButton disabled={isSubmitting} onClick={onClose} />}
                                right={
                                    <NwSaveButton htmlType="submit" disabled={isSubmitting} loading={isSubmitting} />
                                }
                            />
                        </NwForm>
                    )}
                </Formik>
            </NwCard>
        </FormViewWrapper>
    );
};

export default AddCallSheetFormView;
